import React from "react";
import { ArrowUpRight } from "lucide-react";
import { useReveal } from "../../hooks/useReveal";
import { Ornament, StarGlyph, CuneiformMotif, BeeGlyph, HexDivider } from "../Ornaments";

export const MoveThrus = () => {
  const head = useReveal();
  const card = useReveal();

  return (
    <section
      id="move-thrus"
      data-testid="move-thrus-section"
      style={{
        position: "relative",
        padding: "7rem 1.5rem",
        background:
          "linear-gradient(180deg, #0a0806 0%, #0d1326 45%, #14100c 100%)",
        overflow: "hidden",
      }}
    >
      {/* Atmospheric glows — lapis-dominant with a gold accent */}
      <div
        className="lapis-glow"
        style={{ width: 720, height: 720, top: -180, left: -180, opacity: 0.75 }}
      />
      <div
        className="gold-glow"
        style={{ width: 520, height: 520, bottom: -150, right: -120, opacity: 0.4 }}
      />

      <div className="section-inner" style={{ maxWidth: 1080, position: "relative" }}>
        {/* ---- HEAD ---- */}
        <div
          ref={head.ref}
          className={`reveal ${head.inView ? "in-view" : ""}`}
          style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 3.5rem" }}
        >
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Ornament centered />
          </div>
          <p
            className="font-heading"
            style={{
              color: "var(--gold)",
              fontSize: "0.7rem",
              letterSpacing: "0.4em",
              marginBottom: "1rem",
            }}
          >
            The Workshop
          </p>
          <h2 className="section-title" data-testid="move-thrus-title">
            MOVE&nbsp;THRUS<span style={{ fontSize: "0.55em", verticalAlign: "super", marginLeft: "0.15em" }}>™</span>
          </h2>
          <p
            className="italic-quote"
            style={{
              color: "var(--text-muted)",
              maxWidth: 580,
              margin: "0 auto",
              lineHeight: 1.8,
            }}
          >
            A virtual somatic education workshop by Somatic Equilibrium.
          </p>
        </div>

        {/* ---- CARD ---- */}
        <div
          ref={card.ref}
          className={`reveal ${card.inView ? "in-view" : ""}`}
          data-testid="move-thrus-card"
          style={{
            position: "relative",
            padding: "3rem 2.5rem 3.25rem",
            border: "1px solid rgba(61, 106, 168, 0.45)",
            background:
              "linear-gradient(160deg, rgba(26,58,106,0.55) 0%, rgba(20,16,12,0.92) 55%, rgba(10,8,6,0.98) 100%)",
            boxShadow:
              "0 30px 90px -25px rgba(0,0,0,0.9), inset 0 1px 0 rgba(201,168,76,0.08)",
            textAlign: "center",
            overflow: "hidden",
          }}
        >
          {/* Top + bottom gold accent bars */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              left: "10%",
              right: "10%",
              height: 2,
              background:
                "linear-gradient(90deg, transparent 0%, var(--gold) 50%, transparent 100%)",
              opacity: 0.55,
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              bottom: 0,
              left: "10%",
              right: "10%",
              height: 2,
              background:
                "linear-gradient(90deg, transparent 0%, var(--gold) 50%, transparent 100%)",
              opacity: 0.35,
            }}
          />

          {/* Real macro honeycomb photo behind the card */}
          <div
            className="honeycomb-panel"
            style={{ opacity: 0.45, backgroundImage: "url('/media/honeycomb-real.jpg')", backgroundSize: "550px" }}
            aria-hidden="true"
          />

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "0.85rem",
              marginBottom: "1.5rem",
              position: "relative",
            }}
          >
            <StarGlyph size={14} />
            <BeeGlyph size={18} />
            <p
              className="font-heading"
              style={{
                color: "var(--gold)",
                fontSize: "0.62rem",
                letterSpacing: "0.42em",
                margin: 0,
              }}
            >
              Cross the Integration Gap
            </p>
            <BeeGlyph size={18} />
            <StarGlyph size={14} />
          </div>

          <h3
            className="font-display"
            data-testid="move-thrus-headline"
            style={{
              color: "var(--gold-bright)",
              fontSize: "clamp(1.65rem, 3.4vw, 2.55rem)",
              fontWeight: 700,
              lineHeight: 1.15,
              margin: "0 auto 1.25rem",
              maxWidth: 720,
            }}
          >
            MOVE&nbsp;THRUS<span style={{ fontSize: "0.5em", verticalAlign: "super", marginLeft: "0.15em" }}>™</span> &mdash; Cross the Integration Gap
          </h3>

          <p
            style={{
              color: "var(--text)",
              fontSize: "1.02rem",
              lineHeight: 1.8,
              margin: "0 auto 0.85rem",
              maxWidth: 620,
              fontFamily: "var(--font-body)",
            }}
          >
            A virtual somatic education workshop by Somatic Equilibrium.
          </p>

          <p
            style={{
              color: "var(--text-muted)",
              fontSize: "0.92rem",
              lineHeight: 1.85,
              margin: "0 auto 2.5rem",
              maxWidth: 560,
              fontFamily: "var(--font-body)",
              fontStyle: "italic",
            }}
          >
            Bridge what you know in your mind with what your body actually does. Practical movement, lived integration, in community.
          </p>

          <div style={{ display: "flex", justifyContent: "center" }}>
            <a
              href="https://movethrus.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-angled"
              data-testid="move-thrus-landing-link"
            >
              Explore the Workshop
              <ArrowUpRight size={14} color="#0a0806" />
            </a>
          </div>

          <div style={{ marginTop: "2.25rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.6rem", position: "relative" }}>
            <HexDivider width={200} />
            <CuneiformMotif width={180} />
          </div>

          <p
            className="font-heading"
            style={{
              color: "var(--text-dim)",
              fontSize: "0.6rem",
              letterSpacing: "0.35em",
              marginTop: "1rem",
              marginBottom: 0,
            }}
          >
            Opens in a new tab &nbsp;·&nbsp; movethrus.com
          </p>
        </div>
      </div>
    </section>
  );
};
